import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Save, X } from "lucide-react";
import { Button, Card, Input, Label, Badge, Spinner } from "../components/common/UIKit";
import { fetchSettings, saveSettings, fetchAvailableStates } from "../api/adminSettings";

export default function AdminSettingsPage() {
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [states, setStates] = useState([]);
  const [query, setQuery] = useState("");

  const load = async (em, pw) => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([fetchSettings(em, pw), fetchAvailableStates(em, pw)]);
      setSettings(s);
      setStates(st);
      setAuthed(true);
      toast.success("Signed in as admin");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Invalid admin credentials" : "Failed to load");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    await load(email, password);
  };

  const toggleState = (code) => {
    setSettings((s) => ({
      ...s,
      allowed_states: s.allowed_states.includes(code)
        ? s.allowed_states.filter((x) => x !== code)
        : [...s.allowed_states, code],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await saveSettings(email, password, {
        platform_name: settings.platform_name,
        price_per_lead_type: Number(settings.price_per_lead_type),
        price_all_types: Number(settings.price_all_types),
        maintenance_mode: settings.maintenance_mode,
        allowed_states: settings.allowed_states,
      });
      setSettings(updated);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <form onSubmit={login} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-zinc-950" strokeWidth={2.5} />
            <h1 className="text-2xl font-black tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Admin sign-in
            </h1>
          </div>
          <p className="text-sm text-zinc-500 mb-6">Edit platform settings and allowed states.</p>
          <div className="mb-3">
            <Label>Admin email</Label>
            <Input data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="mb-4">
            <Label>Admin password</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button data-testid="admin-login-button" type="submit" className="w-full" disabled={loading}>
            {loading ? <Spinner /> : "Sign in"}
          </Button>
        </form>
      </div>
    );
  }

  const filtered = query ? states.filter((s) => s.state.toLowerCase().includes(query.toLowerCase()) || s.state_code.toLowerCase().includes(query.toLowerCase())) : states;

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <header className="border-b border-zinc-200 px-8 py-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Admin settings
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Pricing, allowed states, maintenance mode.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setAuthed(false); setSettings(null); setPassword(""); }}>
            <X className="h-4 w-4 mr-1" /> Sign out
          </Button>
          <Button data-testid="admin-save-button" onClick={save} disabled={saving}>
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </header>

      {settings && (
        <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <h2 className="text-lg font-bold tracking-tight mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Platform
            </h2>
            <div className="space-y-3">
              <div><Label>Platform name</Label>
                <Input data-testid="admin-platform-name" value={settings.platform_name} onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Price per lead type ($)</Label>
                  <Input data-testid="admin-price-per-type" type="number" step="0.01" value={settings.price_per_lead_type} onChange={(e) => setSettings({ ...settings, price_per_lead_type: e.target.value })} />
                </div>
                <div><Label>Price all types ($)</Label>
                  <Input data-testid="admin-price-all-types" type="number" step="0.01" value={settings.price_all_types} onChange={(e) => setSettings({ ...settings, price_all_types: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-700 pt-2">
                <input type="checkbox" data-testid="admin-maintenance-toggle" checked={settings.maintenance_mode} onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })} className="rounded border-zinc-300" />
                Maintenance mode
              </label>
              <div className="pt-3 border-t border-zinc-100">
                <Label>Public frontend URL (Stripe redirect host)</Label>
                <Input
                  data-testid="admin-public-frontend-url"
                  value={settings.public_frontend_url || ""}
                  onChange={(e) => setSettings({ ...settings, public_frontend_url: e.target.value })}
                  placeholder="https://your-app.example.com"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Stripe `success_url` / `cancel_url` / webhook_url use this host. Overrides the `PUBLIC_FRONTEND_URL` env var.
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Allowed states
                <span className="ml-2 text-xs font-medium text-zinc-500">
                  ({settings.allowed_states.length} / {states.length})
                </span>
              </h2>
              <div className="flex gap-1">
                <Button variant="outline" className="h-7 px-2 text-xs" onClick={() => setSettings({ ...settings, allowed_states: states.map((s) => s.state_code) })}>All</Button>
                <Button variant="outline" className="h-7 px-2 text-xs" onClick={() => setSettings({ ...settings, allowed_states: [] })}>None</Button>
              </div>
            </div>
            <Input data-testid="admin-state-search" placeholder="Search states..." value={query} onChange={(e) => setQuery(e.target.value)} className="mb-3" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-96 overflow-auto">
              {filtered.map((s) => {
                const active = settings.allowed_states.includes(s.state_code);
                return (
                  <button
                    key={s.state_code}
                    type="button"
                    data-testid={`admin-state-toggle-${s.state_code}`}
                    onClick={() => toggleState(s.state_code)}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                      active
                        ? "bg-zinc-950 text-white border-zinc-950"
                        : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
                    }`}
                  >
                    <span className="truncate">{s.state}</span>
                    <span className={`ml-2 font-mono ${active ? "text-zinc-300" : "text-zinc-400"}`}>
                      {s.state_code}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap gap-1">
              {settings.allowed_states.map((code) => (
                <Badge key={code} color="zinc">{code}</Badge>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
